import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, CheckCircle, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchCategories, fetchTopSites } from '../store/actions/siteActions';
import { submitSite } from '../services/submitServices';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Header = styled(motion.div)`
  margin-bottom: 0.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(234, 179, 8, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gold};
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 3.25rem;
`;

const SuccessCard = styled(motion.div)`
  margin-top: 2.5rem;
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(234, 179, 8, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.gold};
`;

const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 1rem 0 0 0;
`;

const SuccessDesc = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

const BackHomeBtn = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  display: block;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  transition: all 0.2s;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  resize: none;
  transition: all 0.2s;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  height: 3rem;
  padding: 0 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  appearance: none;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

function SubmitSite() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ domain: '', description: '', category: '', email: '', alternativeTo: '' });
  const { categories, topSites } = useSelector((state) => state.sites);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchTopSites({ limit: 50 }));
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await submitSite(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('submitPage.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <SeoHead title={t('submitPage.seoTitle')} description={t('submitPage.seoDesc')} noindex />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', maxWidth: '42rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>

        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <HeaderRow>
            <IconBox>
              <Globe size={20} strokeWidth={2.5} />
            </IconBox>
            <Title>{t('submitPage.pageTitle')}</Title>
          </HeaderRow>
          <Subtitle>{t('submitPage.pageSubtitle')}</Subtitle>
        </Header>

        {submitted ? (
          <SuccessCard
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <SuccessIcon>
              <CheckCircle size={32} />
            </SuccessIcon>
            <SuccessTitle>{t('submitPage.successTitle')}</SuccessTitle>
            <SuccessDesc>
              {t('submitPage.successReviewPrefix')} <strong>{form.domain}</strong> {t('submitPage.successReviewSuffix')}
            </SuccessDesc>
            <BackHomeBtn to={prependPath('/')}>{t('submitPage.backHome')}</BackHomeBtn>
          </SuccessCard>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <p style={{ color: 'hsl(0, 84%, 60%)', margin: '0 0 0.25rem 0' }}>{error}</p>}
            <div>
              <Label>{t('submitPage.domainLabel')}</Label>
              <Input
                type="text"
                required
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder={t('submitPage.domainPlaceholder')}
              />
            </div>
            <div>
              <Label>{t('submitPage.descriptionLabel')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('submitPage.descriptionPlaceholder')}
                rows={3}
              />
            </div>
            <div>
              <Label>{t('submitPage.categoryLabel')}</Label>
              <Select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">{t('submitPage.categorySelect')}</option>
                {categories.map((cat) => (
                  <option key={cat._id || cat.slug} value={cat._id || cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>{t('submitPage.alternativeTo')}</Label>
              <Select
                value={form.alternativeTo}
                onChange={(e) => setForm({ ...form, alternativeTo: e.target.value })}
              >
                <option value="">{t('submitPage.alternativeNone')}</option>
                {topSites.map((s) => (
                  <option key={s.id} value={s.domain}>{s.title} ({s.domain})</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>{t('submitPage.emailLabel')}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t('submitPage.emailPlaceholder')}
              />
            </div>
            <SubmitBtn type="submit" disabled={loading}>
              <Send size={16} strokeWidth={2.5} /> {t('submitPage.submitButton')}
            </SubmitBtn>
          </Form>
        )}
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default SubmitSite;
